import {
  createRouter,
  createWebHistory,
  RouteRecordRaw,
  RouteLocationNormalized,
  NavigationGuardNext,
} from "vue-router";
import { isAuthenticated, getUserRole } from "@/services/authService";

const routes: Array<RouteRecordRaw> = [
  {
    path: "/",
    name: "home",
    component: () => import("../views/customer/ProductListView.vue"),
    meta: {
      title: "物販一覧",
    },
  },
  // 事前申し込み終了のため、カート画面へのルートを無効化
  // {
  //   path: "/cart",
  //   name: "cart",
  //   component: () => import("../views/customer/CartView.vue"),
  //   meta: {
  //     title: "カート・注文確認",
  //   },
  // },
  {
    path: "/complete/:orderId",
    name: "complete",
    component: () => import("../views/customer/OrderCompleteView.vue"),
    meta: {
      title: "予約完了",
    },
  },
  {
    path: "/cancel/execute/:token",
    name: "cancel-execute",
    component: () => import("../views/customer/CancelExecuteView.vue"),
    meta: {
      title: "キャンセル確認",
    },
  },
  {
    path: "/privacy-policy",
    name: "privacy-policy",
    component: () => import("../views/PrivacyPolicyView.vue"),
    meta: {
      title: "プライバシーポリシー",
    },
  },
  // スタッフ用ページ
  {
    path: "/backyard",
    name: "backyard",
    component: () => import("../views/backyard/DashboardView.vue"),
    meta: {
      title: "ダッシュボード",
      requiresAuth: true,
      requiresRole: ["admin", "staff", "member"], // 全員アクセス可能
      robots: "noindex, nofollow",
    },
  },
  {
    path: "/backyard/callback",
    name: "backyard-callback",
    component: () => import("../views/backyard/AuthCallbackView.vue"),
    meta: {
      title: "ログイン処理中",
      robots: "noindex, nofollow",
    },
  },
  {
    path: "/backyard/orders",
    name: "backyard-orders",
    component: () => import("../views/backyard/OrderListView.vue"),
    meta: {
      title: "注文一覧 - スタッフ",
      requiresAuth: true,
      requiresRole: ["admin", "staff"], // adminとstaffのみアクセス可能（memberは不可）
      robots: "noindex, nofollow",
    },
  },
  {
    path: "/backyard/items",
    name: "backyard-items",
    component: () => import("../views/backyard/ItemSummaryView.vue"),
    meta: {
      title: "アイテム集計",
      requiresAuth: true,
      requiresRole: ["admin", "staff", "member"], // 全員アクセス可能
      robots: "noindex, nofollow",
    },
  },
  // 404ページ
  {
    path: "/:pathMatch(.*)*",
    name: "not-found",
    component: () => import("../views/NotFoundView.vue"),
    meta: {
      title: "ページが見つかりません",
    },
  },
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
  scrollBehavior(
    to: RouteLocationNormalized,
    from: RouteLocationNormalized,
    savedPosition: { left: number; top: number } | null
  ) {
    // 常にページトップにスクロール
    if (savedPosition) {
      return savedPosition;
    } else {
      return { top: 0 };
    }
  },
});

// 認証チェックとページタイトルの設定
router.beforeEach(
  (
    to: RouteLocationNormalized,
    from: RouteLocationNormalized,
    next: NavigationGuardNext
  ) => {
    // ページタイトルの設定
    const title = to.meta.title as string;
    if (title) {
      document.title = `${title} | 物販予約システム`;
    } else {
      document.title = "物販予約システム";
    }

    // robotsメタタグの設定
    const robots = to.meta.robots as string;
    let robotsMetaTag = document.querySelector('meta[name="robots"]');

    if (robots) {
      // robotsメタタグが存在しない場合は作成
      if (!robotsMetaTag) {
        robotsMetaTag = document.createElement("meta");
        robotsMetaTag.setAttribute("name", "robots");
        document.head.appendChild(robotsMetaTag);
      }
      robotsMetaTag.setAttribute("content", robots);
    } else if (robotsMetaTag) {
      // robotsメタタグが不要な場合は削除
      robotsMetaTag.remove();
    }

    // 認証が必要なページの場合
    if (to.meta.requiresAuth) {
      if (!isAuthenticated()) {
        // 未認証の場合、認証サービスのloginを呼ぶ
        import("@/services/authService").then((authService) => {
          authService.login();
        });
        return;
      }

      // ロールチェック
      const requiresRole = to.meta.requiresRole as string[] | undefined;
      if (requiresRole && requiresRole.length > 0) {
        const userRole = getUserRole();
        if (!userRole || !requiresRole.includes(userRole)) {
          // 権限がない場合、ホームページにリダイレクト
          alert(
            "このページにアクセスする権限がありません。\n" +
              `必要な権限: ${requiresRole.join(" または ")}\n` +
              `あなたの権限: ${userRole || "なし"}`
          );
          next({ name: "home" });
          return;
        }
      }
    }

    next();
  }
);

export default router;
